// src/pages/assignment/AssignFieldSupervisorList.jsx
import React, { useState, useEffect } from 'react';
import { Users, Eye, Search, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../components/common/DataTable';
import SupervisorDetailPage from './SupervisorDetailPage';
import fieldSupervisorService from '../../services/fieldSupervisorService';

const AssignFieldSupervisorList = () => {
  const [view, setView] = useState('list'); // 'list', 'detail'
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'fullName', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');

  // Fetch supervisors on mount
  useEffect(() => {
    fetchSupervisors();
  }, []);

  // Fetch all supervisors
  const fetchSupervisors = async () => {
    setIsLoading(true);
    try {
      const data = await fieldSupervisorService.getAllSupervisors();
      setSupervisors(data);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      // Error already handled by axios interceptor with toast
    } finally {
      setIsLoading(false);
    }
  };

  // Filter supervisors based on search
  const filteredSupervisors = supervisors.filter(supervisor =>
    supervisor.fullName.toLowerCase().includes(searchValue.toLowerCase()) ||
    supervisor.username.toLowerCase().includes(searchValue.toLowerCase()) ||
    supervisor.email.toLowerCase().includes(searchValue.toLowerCase()) ||
    supervisor.role.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Sort supervisors
  const sortedSupervisors = [...filteredSupervisors].sort((a, b) => {
    const aValue = a[sortConfig.sortBy];
    const bValue = b[sortConfig.sortBy];
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate supervisors
  const startIndex = currentPage * pageSize;
  const paginatedSupervisors = sortedSupervisors.slice(startIndex, startIndex + pageSize);

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'fullName',
      label: 'Supervisor Name',
      sortable: true,
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.fullName}</div>
          <div className="text-xs text-gray-500 mt-1">
            <span className="inline-flex items-center gap-1">
              <span className="font-medium">{row.username}</span>
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          row.role === 'Supervisor' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {row.role}
        </span>
      )
    },
    {
      key: 'email',
      label: 'Contact',
      sortable: false,
      render: (row) => (
        <div className="text-sm">
          <div className="text-gray-900">{row.email}</div>
          {row.phone && (
            <div className="text-gray-500 mt-1">{row.phone}</div>
          )}
        </div>
      )
    },
    {
      key: 'totalSegments',
      label: 'FO Segments',
      sortable: true,
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-800 font-bold text-lg">
            {row.totalSegments}
          </span>
        </div>
      )
    },
    {
      key: 'totalSites',
      label: 'Tower Sites',
      sortable: true,
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-800 font-bold text-lg">
            {row.totalSites}
          </span>
        </div>
      )
    },
    {
      key: 'totalAssignments',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <div className="text-center">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-red-600 text-white font-bold text-lg shadow-md">
            {row.totalAssignments}
          </span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          row.status === 'ACTIVE' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {row.status}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewDetail(row)}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleViewDetail = (supervisor) => {
    setSelectedSupervisor(supervisor);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedSupervisor(null);
    fetchSupervisors(); // Refresh list
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  // Render Detail View
  if (view === 'detail' && selectedSupervisor) {
    return (
      <SupervisorDetailPage
        supervisorId={selectedSupervisor.id}
        onBack={handleBackToList}
      />
    );
  }

  // Render List View (Default)
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Field Supervisor Assignment</h1>
              <p className="text-gray-600 mt-1">Halaman untuk mengelola Penugasan Supervisor Lapangan</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
            <UserCheck className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-semibold text-blue-800">
              {supervisors.length} Active Supervisors
            </span>
          </div>
        </div>
      </div>

      {/* DataTable Component */}
      <DataTable
        columns={columns}
        data={paginatedSupervisors}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={filteredSupervisors.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={isLoading}
        onSearch={handleSearch}
        searchValue={searchValue}
        searchPlaceholder="Search by name, username, email, or role..."
        enableQuickJump={true}
        customPageSizes={[5, 10, 25, 50]}
        striped={false}
        headerBgColor="bg-gradient-to-r from-blue-600 to-blue-500"
        headerTextColor="text-white"
        hoverColor="hover:bg-blue-50"
      />
    </div>
  );
};

export default AssignFieldSupervisorList;